import {DailyBehaviourV2, DailyCheckinsV2} from '@shared/api/models/query/patient-daily-snapshot.model';
import {
    CarerWeeklyAdherence,
    CarerWeeklySnapshotV2,
    isMealPlanV2,
    PatientWeeklyAdherenceV2,
    PatientWeeklySnapshotV2,
    WeeklyMealPlanSideV2,
    WeeklySnapshotV2
} from '@shared/api/models/query/patient-weekly-snapshot.model';

/**
 * Placeholder data for the Daily and Weekly detail pages when the backend returns no
 * statistics for the selected day/week (`{"data": {"data": null, "noData": true}}`,
 * UNIVAPLAT-3259).
 *
 * The pages keep their full layout and feed it these empty values, with the content block
 * disabled (see `_detail-no-data.scss`), instead of replacing everything with a standalone
 * "No data available" card. Labels are fixed placeholders only — real behaviour names come
 * from the payload once data exists.
 */

/** Behaviour rows shown disabled on both Daily pages and the Weekly patient page. */
const PATIENT_PLACEHOLDER_BEHAVIOURS = ['Used laxatives', 'Binged', 'Purged', 'Compulsive exercise'];

/** Behaviour rows shown disabled on the Weekly carer page ("Observed behaviours"). */
const CARER_WEEKLY_PLACEHOLDER_BEHAVIOURS = ['Used laxatives', 'Purged', 'Restricted food'];

export type DetailPlaceholderVariant = 'patient' | 'carer';

export function emptyBehaviourRows(names: string[]): DailyBehaviourV2[] {
    return names.map(name => ({name, counter: 0, severity: 'grey', trend: null, percentage: 0}));
}

export function emptyDailyBehaviours(): DailyBehaviourV2[] {
    return emptyBehaviourRows(PATIENT_PLACEHOLDER_BEHAVIOURS);
}

export function emptyWeeklyBehaviours(variant: DetailPlaceholderVariant): DailyBehaviourV2[] {
    return emptyBehaviourRows(variant === 'patient' ? PATIENT_PLACEHOLDER_BEHAVIOURS : CARER_WEEKLY_PLACEHOLDER_BEHAVIOURS);
}

/** Stress / Mood / Sleep all unanswered (the card adds the patient-only Contributing factors row itself). */
export function emptyDailyCheckins(): DailyCheckinsV2 {
    return {
        stress: {value: null, label: null, answered: false},
        mood: {core: null, fine: null, answered: false},
        sleep: {feltState: null, factors: [], answered: false},
        contributingFactors: [],
    };
}

/** Zero adherence — renders the three/two donuts at 0%. */
export function emptyWeeklyAdherence(variant: 'patient'): PatientWeeklyAdherenceV2;
export function emptyWeeklyAdherence(variant: 'carer'): CarerWeeklyAdherence;
export function emptyWeeklyAdherence(variant: DetailPlaceholderVariant): PatientWeeklyAdherenceV2 | CarerWeeklyAdherence {
    const base = {chapters: {scheduled: 0, completed: 0}, checkIns: {scheduled: 0, completed: 0}};
    return variant === 'patient' ? {...base, foodDiary: {scheduled: 0, completed: 0}} : base;
}

/**
 * True when a v2 weekly snapshot exists but nothing was reported or completed that week
 * (UNIVAPLAT-3259, "case 3"). The page treats it exactly like a missing snapshot, so both
 * render the same disabled layout. Scheduled-but-not-done items do not count as data:
 * 0 completed out of N scheduled is still an empty week.
 */
export function isEmptyWeeklySnapshot(snapshot: WeeklySnapshotV2): boolean {
    const {adherence, dailyCheckins: checkins} = snapshot;
    const nothingCompleted = adherence.chapters.completed === 0
        && adherence.checkIns.completed === 0
        && (!('foodDiary' in adherence) || adherence.foodDiary.completed === 0);
    const noCheckins = checkins.mood.answeredDays === 0
        && checkins.sleep.answeredDays === 0
        && checkins.edBehaviourAnsweredDays === 0
        && checkins.edBehaviourCounter === 0
        && Object.values(checkins.stress.values ?? {}).every(value => value === null);
    const noBehaviours = snapshot.observedBehaviours.every(b => b.patient.value === 0 && b.carer.value === 0);
    const noChapterProgress = snapshot.chapters.every(c => !c.completed && c.progressValue === 0);
    if (!(nothingCompleted && noCheckins && noBehaviours && noChapterProgress)) return false;

    return 'surveys' in snapshot
        ? isEmptyPatientExtras(snapshot as PatientWeeklySnapshotV2)
        : isEmptyCarerExtras(snapshot as CarerWeeklySnapshotV2);
}

function isEmptyPatientExtras(snapshot: PatientWeeklySnapshotV2): boolean {
    return snapshot.surveys === null;
}

function isEmptyCarerExtras(snapshot: CarerWeeklySnapshotV2): boolean {
    const noObservations = Object.values(snapshot.observations ?? {}).every(area => area?.direction == null);
    const noHowItsGoing = snapshot.howItsGoing?.progressFeeling == null && snapshot.howItsGoing?.univaHelpfulness == null;
    return noObservations && noHowItsGoing && isEmptyMealPlan(snapshot.mealPlan);
}

function isEmptyMealPlan(mealPlan: CarerWeeklySnapshotV2['mealPlan']): boolean {
    if (!mealPlan) return true;
    if (!isMealPlanV2(mealPlan)) return mealPlan.challenges.length === 0 && mealPlan.wins.length === 0;
    // An explicit "nothing this week" is a carer answer, so it counts as data.
    const sideEmpty = (side: WeeklyMealPlanSideV2) => side.items.length === 0 && !side.somethingElse && !side.nothingThisWeek;
    return sideEmpty(mealPlan.challenges) && sideEmpty(mealPlan.wins);
}
